import { useState } from 'react';
import { Button, Input } from '@dataroom/shared';
import type { IssueEnvelopeRequest, RoomDocument } from '@dataroom/shared';
import type { RoomActor } from '@/api/hooks';
import { Modal } from './Modal';

interface IssueAgreementDialogProps {
  /** Room members to pick a signer from; empty → manual actor-id entry. */
  actors: RoomActor[];
  /** Room documents, for the per-buyer redline override picker. */
  documents: RoomDocument[];
  /** The room's master NDA document id — issued by default. */
  masterNdaDocumentId: string | null;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: IssueEnvelopeRequest) => void;
}

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';

/**
 * PRD-0004 §3c "Issue agreement" — the admin issues the room's NDA to a buyer.
 * One master NDA per room, so there is no type or document choice by default: it
 * issues the master NDA. A buyer who redlined can be issued a DIFFERENT document
 * via the override. Presentational: the page owns the mutation and pending/error.
 */
export function IssueAgreementDialog({
  actors,
  documents,
  masterNdaDocumentId,
  pending,
  error,
  onClose,
  onSubmit,
}: IssueAgreementDialogProps) {
  const [signerActorId, setSignerActorId] = useState(actors[0]?.id ?? '');
  const [useRedline, setUseRedline] = useState(false);
  const masterDoc = documents.find((d) => d.id === masterNdaDocumentId) ?? null;
  // Redline options exclude the master itself (issuing the master isn't a redline).
  const redlineOptions = documents.filter((d) => d.id !== masterNdaDocumentId);
  const [redlineDocId, setRedlineDocId] = useState(redlineOptions[0]?.id ?? '');

  const documentId = useRedline ? redlineDocId : (masterNdaDocumentId ?? '');
  const canSubmit = signerActorId.trim().length > 0 && documentId.length > 0 && !pending;

  return (
    <Modal
      title="Issue the NDA"
      description="Send the room's NDA to a buyer to sign. Sealing it advances their funnel and unlocks the CIM."
      onClose={onClose}
      footer={
        <>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <span className="text-xs text-muted-foreground">The buyer signs it from their portal.</span>
          )}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!canSubmit}
              onClick={() => onSubmit({ documentId, signerActorId: signerActorId.trim() })}
            >
              {pending ? 'Issuing…' : 'Issue'}
            </Button>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="issue-signer" className="text-sm font-medium">
            Signer (buyer)
          </label>
          {actors.length > 0 ? (
            <select
              id="issue-signer"
              className={selectClass}
              value={signerActorId}
              onChange={(e) => setSignerActorId(e.target.value)}
              disabled={pending}
            >
              {actors.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          ) : (
            <Input
              id="issue-signer"
              placeholder="Actor ID (UUID)"
              value={signerActorId}
              onChange={(e) => setSignerActorId(e.target.value)}
              disabled={pending}
            />
          )}
        </div>

        {/* The master NDA is the default; a redline override lets a specific buyer
            get a different (negotiated) document instead. */}
        <div className="space-y-1.5">
          <span className="text-sm font-medium">Document</span>
          <p className="text-sm">
            {masterDoc ? (
              <>
                Master NDA — <span className="font-medium">{masterDoc.name}</span>
              </>
            ) : (
              <span className="text-destructive">
                No master NDA set for this room. Set one on the Agreements panel first.
              </span>
            )}
          </p>
          {masterDoc ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={useRedline}
                disabled={pending || redlineOptions.length === 0}
                onChange={(e) => setUseRedline(e.target.checked)}
              />
              Issue a different document for this buyer (redline)
            </label>
          ) : null}
          {useRedline ? (
            redlineOptions.length > 0 ? (
              <select
                aria-label="Redline document"
                className={selectClass}
                value={redlineDocId}
                onChange={(e) => setRedlineDocId(e.target.value)}
                disabled={pending}
              >
                {redlineOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-muted-foreground">
                Upload the buyer&apos;s redlined document to the room first.
              </p>
            )
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
